import React, { useEffect, useState } from "react";
import DeviceCard from "./DeviceCard";
import {
  Box,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
} from "@mui/material";
import { getListAcm } from "../../services/info/info";
import { AnimatePresence, motion } from "framer-motion"; // Import Framer Motion
import { useTranslation } from "react-i18next";

function Device() {
  const [deviceData, setDeviceData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedAddress, setSelectedAddress] = useState("");
  const { t } = useTranslation();

  const handleChange = (event) => {
    setSelectedAddress(event.target.value);
  };

  const fetchData = async (userLat, userLng) => {
    try {
      const response = await getListAcm();
      let sortedData;

      if (userLat !== null && userLng !== null) {
        // Calculate distances and sort
        sortedData = response.data
          .map((device) => {
            const distance = calculateDistance(
              userLat,
              userLng,
              parseFloat(device.latAcm),
              parseFloat(device.lngAcm)
            );
            return { ...device, distance };
          })
          .sort((a, b) => a.distance - b.distance);
      } else {
        // Skip distance calculation
        sortedData = response.data.map((device) => ({
          ...device,
          distance: null,
        }));
      }

      setDeviceData(sortedData);
      setIsLoading(false);
    } catch (error) {
      console.error("Error fetching device data:", error);
      setIsLoading(false);
    }
  };

  const calculateDistance = (lat1, lon1, lat2, lon2) => {
    const toRadians = (angle) => (angle * Math.PI) / 180;
    const R = 6371; // Earth's radius in kilometers
    const dLat = toRadians(lat2 - lat1);
    const dLon = toRadians(lon2 - lon1);
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(toRadians(lat1)) *
        Math.cos(toRadians(lat2)) *
        Math.sin(dLon / 2) *
        Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c; // Distance in kilometers
  };

  const getUserLocation = () => {
    if (!navigator.geolocation) {
      console.error("Geolocation is not supported by this browser.");
      fetchData(null, null);
      return;
    }

    const geoPromise = new Promise((resolve, reject) => {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          resolve(position);
        },
        (error) => {
          console.error("Error getting user location:", error);
          reject(error);
        }
      );
    });

    const timeoutPromise = new Promise(
      (_, reject) =>
        setTimeout(() => reject(new Error("موقعیت مکانی یافت نشد")), 2500) // 2 seconds
    );

    Promise.race([geoPromise, timeoutPromise])
      .then((position) => {
        const { latitude, longitude } = position.coords;
        fetchData(latitude, longitude);
      })
      .catch((error) => {
        console.warn("Using fallback location handling:", error.message);
        fetchData(null, null);
      });
  };

  useEffect(() => {
    getUserLocation();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Filter deviceData based on the selected address
  const filteredData = selectedAddress
    ? deviceData.filter((device) => device.addresAcm.includes(selectedAddress))
    : deviceData;

  return (
    <>
      <Box className="p-3">
        {isLoading ? (
          <Box
            sx={{
              height: "50vh",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <CircularProgress color="primary" size={70} />
          </Box>
        ) : (
          <AnimatePresence>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                marginTop: "1rem",
              }}
            ></Box>
            <FormControl fullWidth>
              <InputLabel id="demo-simple-select-label">
                {t("Device.Select")}
              </InputLabel>
              <Select
                labelId="demo-simple-select-label"
                id="demo-simple-select"
                value={selectedAddress}
                label={t("Device.Select")}
                onChange={handleChange}
              >
                <MenuItem value="">{t("Device.All")}</MenuItem>
                <MenuItem value={t("Device.Bushehr")}>
                  {t("Device.Bushehr")}
                </MenuItem>
                <MenuItem value={t("Device.Shiraz")}>
                  {t("Device.Shiraz")}
                </MenuItem>
                <MenuItem value={t("Device.Tehran")}>
                  {t("Device.Tehran")}
                </MenuItem>
                <MenuItem value={t("Device.Qom")}>{t("Device.Qom")}</MenuItem>
              </Select>
            </FormControl>
            {filteredData.map((device, index) => (
              <motion.div
                key={device.codeAcm + index}
                initial={{ opacity: 0, x: 10 }} // Initial animation state (fade in + slide from bottom)
                animate={{ opacity: 1, x: 0 }} // Animation on mount
                exit={{ opacity: 0, x: -10 }} // Exit animation on unmount (fade out + slide up)
                transition={{
                  duration: 0.5, // Enter animation duration
                  delay: 0.2 * index, // Add a delay for each subsequent item based on its index
                }}
              >
                <DeviceCard
                  codeAcm={device.codeAcm}
                  addresAcm={device.addresAcm}
                  statueAcm={device.statueAcm}
                  mappath={device.mappath}
                />
              </motion.div>
            ))}
          </AnimatePresence>
        )}
      </Box>
    </>
  );
}

export default Device;
